import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/api';

/**
 * Custom hook for fetching and managing AI-generated portfolio insights
 * Used by V2 Insights page for displaying daily insights feed
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: true)
 * @param {number} options.autoRefreshInterval - Auto-refresh interval in ms (default: 120000)
 * @returns {Object} Insights data and utility functions
 */
export function useInsights(options = {}) {
  const {
    autoFetch = true,
    autoRefreshInterval = 120000, // 2 minutes
  } = options;

  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filters, setFilters] = useState({
    category: null,
    unreadOnly: false,
  });

  // Store pagination key for load more functionality
  const nextKeyRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  /**
   * Fetch insights from API
   * @param {Object} params - Fetch parameters
   * @param {boolean} params.reset - Whether to reset pagination (default: true)
   * @param {boolean} params.silent - Whether to suppress loading state (for auto-refresh)
   */
  const fetchInsights = useCallback(async ({ reset = true, silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);

      const params = new URLSearchParams({
        limit: '20',
      });

      // Add filters
      if (filters.category) {
        params.append('category', filters.category);
      }
      if (filters.unreadOnly) {
        params.append('unreadOnly', 'true');
      }

      // Add pagination key if loading more
      if (!reset && nextKeyRef.current) {
        params.append('lastKey', nextKeyRef.current);
      }

      const response = await axios.get(`${API_BASE_URL}/insights?${params.toString()}`, {
        withCredentials: true,
      });

      const { insights: fetchedInsights, hasMore: more, nextKey, unreadCount: fetchedUnreadCount } = response.data;

      if (reset) {
        setInsights(fetchedInsights || []);
      } else {
        setInsights(prev => [...prev, ...(fetchedInsights || [])]);
      }

      setHasMore(more);
      setUnreadCount(fetchedUnreadCount || 0);
      nextKeyRef.current = nextKey || null;
      setLoading(false);

      return response.data;
    } catch (err) {
      console.error('Error fetching insights:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load insights');
      setLoading(false);
      throw err;
    }
  }, [filters]);

  /**
   * Load more insights (pagination)
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchInsights({ reset: false });
  }, [hasMore, loading, fetchInsights]);

  /**
   * Manual refresh - reset pagination and fetch fresh data
   */
  const refresh = useCallback(async () => {
    nextKeyRef.current = null;
    await fetchInsights({ reset: true });
  }, [fetchInsights]);

  /**
   * Mark a single insight as read
   * @param {string} insightId - The insight ID
   * @param {string} date - The insight date (YYYY-MM-DD)
   */
  const markAsRead = useCallback(async (insightId, date) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/insights/${insightId}/read?date=${date}`,
        {},
        { withCredentials: true }
      );

      // Update local state
      setInsights(prev => prev.map(insight => {
        if (insight.insightId === insightId) {
          return { ...insight, read: true };
        }
        return insight;
      }));

      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));

      // Dispatch event to notify other components (e.g., sidebar)
      window.dispatchEvent(new CustomEvent('insights-unread-count-changed'));
    } catch (err) {
      console.error('Error marking insight as read:', err);
      throw err;
    }
  }, []);

  /**
   * Mark ALL insights as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await axios.patch(
        `${API_BASE_URL}/insights/mark-all-read`,
        {},
        { withCredentials: true }
      );

      // Update local state
      setInsights(prev => prev.map(insight => ({ ...insight, read: true })));
      setUnreadCount(0);

      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('insights-unread-count-changed'));
    } catch (err) {
      console.error('Error marking all insights as read:', err);
      throw err;
    }
  }, []);

  /**
   * Dismiss an insight
   * @param {string} insightId - The insight ID
   * @param {string} date - The insight date (YYYY-MM-DD)
   */
  const dismissInsight = useCallback(async (insightId, date) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/insights/${insightId}/dismiss?date=${date}`,
        {},
        { withCredentials: true }
      );

      // Remove from local state
      setInsights(prev => prev.filter(insight => insight.insightId !== insightId));

      // If it was unread, decrement count
      const wasUnread = insights.find(i => i.insightId === insightId && !i.read);
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
        window.dispatchEvent(new CustomEvent('insights-unread-count-changed'));
      }
    } catch (err) {
      console.error('Error dismissing insight:', err);
      throw err;
    }
  }, [insights]);

  /**
   * Update filters - this will trigger a new fetch
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
    }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({
      category: null,
      unreadOnly: false,
    });
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    if (autoFetch && isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      fetchInsights({ reset: true });
    }
  }, [autoFetch, fetchInsights]);

  // Refetch when filters change (but not on initial load)
  useEffect(() => {
    if (!isInitialLoadRef.current) {
      nextKeyRef.current = null;
      fetchInsights({ reset: true });
    }
  }, [filters, fetchInsights]);

  // Auto-refresh interval
  useEffect(() => {
    if (!autoRefreshInterval) return;

    const interval = setInterval(() => {
      fetchInsights({ reset: true, silent: true });
    }, autoRefreshInterval);

    return () => clearInterval(interval);
  }, [autoRefreshInterval, fetchInsights]);

  return {
    insights,
    loading,
    error,
    hasMore,
    unreadCount,
    filters,
    setFilters: updateFilters,
    loadMore,
    refresh,
    markAsRead,
    markAllAsRead,
    dismissInsight,
    clearFilters,
  };
}

export default useInsights;
